import { useMemo } from 'react';
import { getDevToken } from './devAuth';
import { useConvexCognitoAuth } from './useConvexCognitoAuth';
import { useDevToken } from './useDevToken';

/**
 * Auth source for `ConvexProviderWithAuth`: a stored dev bypass token (local
 * dev / e2e builds only) wins over Cognito; otherwise
 * behaves exactly like the Amplify-backed Cognito hook. Both hooks always run
 * to satisfy the rules of hooks.
 *
 * `getDevToken` discards expired tokens, and starting a real Cognito login
 * clears the stored one (useLogin), so a dead dev session can never shadow
 * Cognito: it either falls back here or is dropped on the way in.
 *
 * Memoised by hand: not every consumer runs the React Compiler, and
 * `ConvexProviderWithAuth` re-runs its auth effect on every new object.
 */
export const useConvexAppAuth = () => {
  const cognitoAuth = useConvexCognitoAuth();
  const devToken = useDevToken();

  return useMemo(() => {
    if (devToken === null) return cognitoAuth;
    return {
      fetchAccessToken: async () => getDevToken(),
      isAuthenticated: true,
      isLoading: false,
    };
  }, [devToken, cognitoAuth]);
};
